import Big from 'big.js';
import { LINE_COLORS } from 'constants/dashboardChart';
import { IPFS_GATEWAY } from 'constants/general';
import { ROUTES } from 'constants/routes';
import {
  HAS_SEEN_INITIAL_FILMINE_ADDRESS_POPUP,
  LS_AUTH_DATA_KEY,
  LS_THEME_DATA_KEY,
  LS_USER_SEEN_PAPERCUPS,
} from 'constants/user';
import { format, fromUnixTime, getUnixTime } from 'date-fns';
import { FormikErrors, FormikTouched } from 'formik';
import _ from 'lodash';
import { ReactElement, ReactText } from 'react';
import { DealDataCenter } from 'state/contexts/DealsContext/DealsContext.types';
import {
  AlgoliaSearchHit,
  AlgoliaSearchStorageProviderHit,
  AlgoliaStorageProvider,
} from 'types/algolia';
import { Theme } from 'types/common';
import { DataCenter } from 'types/dataCenter';
import { ApiStorageProvider, MediaFile } from 'types/storageProvider';
import { Address, RewardsPayout, UserAddress } from 'types/user';
import { CLIENT_ROLE_LINKS, STORAGE_PROVIDER_ROLE_LINKS } from '../constants/dashboard';

/**
 * Mock method to simulate async call
 *
 * @param val {any}
 * @param time {Number}
 * @return {Promise<any>}
 */
type Wait = <T>(time: number, val: T) => Promise<T>;
export const wait: Wait = (time = 500, val) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(val);
    }, time);
  });

/**
 * Formats the review score to 2 decimals
 *
 * @param score {Number|String}
 * @return {String}
 */
type FormatReviewScore = (score: number) => string;
export const formatReviewScore: FormatReviewScore = (score) =>
  parseFloat(`${score}`).toFixed(2);

/**
 * Calculates percentage of the score from the total
 * for the stars
 *
 * @param score {Number}
 * @return {number}
 */
type CalcReviewPercentage = (score: number) => number;
export const calcReviewPercentage: CalcReviewPercentage = (score) => (score * 100) / 5;

/**
 * Checks if the passed string is a JSON object
 *
 * @param str {String}
 * @return {boolean}
 */
type isJsonString = (str: string) => boolean;
export const isJsonString: isJsonString = (str) => {
  if (typeof str !== 'string') return false;
  try {
    const result = JSON.parse(str);
    const type = Object.prototype.toString.call(result);

    return type === '[object Object]' || type === '[object Array]';
  } catch (err) {
    return false;
  }
};

/**
 * Checks if the userData has a storageProvider address that matches
 * the profile you are on
 *
 * @param storageProviderPubKeyAddress {String}
 * @param userAddresses {Array}
 */
type IsUserStorageProviderOwner = (
  ownerAddress: Address,
  userAddresses: UserAddress[],
) => boolean;
export const isUserStorageProviderOwner: IsUserStorageProviderOwner = (
  ownerAddress,
  userAddresses,
) => {
  const found = userAddresses.find(
    ({ pubKeyAddress, IDAddress }) =>
      pubKeyAddress === ownerAddress.pubKeyAddress ||
      IDAddress === ownerAddress.IDAddress,
  );

  return !!found;
};

/**
 * Checks if a form field has an error in the form system
 *
 * @param errors {Object}
 * @param fieldName {String}
 * @param fieldArrayName {String}
 * @param fieldArrayIndex {Number}
 * @param fieldArrayValueProp {String}
 * @return {string|*}
 */
type GetFormFieldError = <T extends { [key: string]: string | string[] }>(
  errors: FormikErrors<T>,
  fieldName: keyof T,
  fieldArrayName: keyof T,
  fieldArrayIndex: number,
  fieldArrayValueProp: ReactText,
) => string;
// TODO - fix errors types after form types are defined
export const getFormFieldError: GetFormFieldError = (
  errors,
  fieldName,
  fieldArrayName,
  fieldArrayIndex,
  fieldArrayValueProp,
) => {
  if (fieldArrayName) {
    if (!errors[fieldArrayName]) return '';
    // @ts-ignore
    if (!errors[fieldArrayName][fieldArrayIndex]) return '';

    // @ts-ignore
    return errors[fieldArrayName][fieldArrayIndex][fieldArrayValueProp];
  }

  return errors[fieldName];
};

/**
 * Checks if a form field has an error and has been touched in the form system
 *
 * @param touched {Object}
 * @param fieldName {String}
 * @param error {String}
 * @param fieldArrayName {String}
 * @param fieldArrayIndex {Number}
 * @param fieldArrayValueProp {String}
 * @return {*}
 */
type GetFormFieldHasTouchedAndError = <T>(
  touched: FormikTouched<T>,
  fieldName: keyof T,
  error: string,
  fieldArrayName: keyof T,
  fieldArrayIndex: number,
  fieldArrayValueProp: ReactText,
) => boolean;
export const getFormFieldHasTouchedAndError: GetFormFieldHasTouchedAndError = (
  touched,
  fieldName,
  error,
  fieldArrayName,
  fieldArrayIndex,
  fieldArrayValueProp,
) => {
  let hasTouched;
  // TODO - fix errors types after form types are defined
  if (fieldArrayName) {
    if (!touched[fieldArrayName]) hasTouched = false;
    // @ts-ignore
    else if (!touched[fieldArrayName][fieldArrayIndex]) hasTouched = false;
    // @ts-ignore
    else hasTouched = touched[fieldArrayName][fieldArrayIndex][fieldArrayValueProp];
  } else {
    hasTouched = touched[fieldName];
  }

  return hasTouched && error;
};

/**
 * Check is error message 404 error
 *
 * @param errorMessage
 * @return {boolean}
 */
type IsErrorMessage404 = (errorMessage: string) => boolean;
export const isErrorMessage404: IsErrorMessage404 = (errorMessage) => {
  return errorMessage === '404 page not found';
};

type CallFunctionAfter = (fnc: Function, timeOut: number) => () => void;
export const callFunctionAfter: CallFunctionAfter = (fnc, timeOut) => {
  const timer = setTimeout(() => {
    if (fnc) fnc();
  }, timeOut); // Delay start animation

  return () => {
    clearTimeout(timer);
  };
};

/**
 * Truncates the address string to the specified length, by replacing the
 * characters in the middle with an ellipsis, i.e. f3qqx...qffc34cc
 */
type TruncateAddressString = (address: string, length: number) => string;
export const truncateAddressString: TruncateAddressString = (address, length) => {
  if (address.length <= length) return address;

  const separator = '...';

  const sepLen = separator.length;
  const charsToShow = length - sepLen;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return (
    address.substr(0, frontChars) + separator + address.substr(address.length - backChars)
  );
};

/**
 * Truncates the public key address of an address object to the specified
 * length, by replacing the characters in the middle with an ellipsis,
 * i.e. f3qqx...qffc34cc
 */
type TruncateAddress = ({ pubKeyAddress, IDAddress }: Address, length: number) => string;
export const truncateAddress: TruncateAddress = (
  { pubKeyAddress, IDAddress },
  length,
) => {
  if (!pubKeyAddress) return IDAddress;

  return truncateAddressString(pubKeyAddress, length);
};

type GetPaginationVisiblePages = (
  page: number,
  onSide: number,
  pagesCount: number,
) => number[];
export const getPaginationVisiblePages: GetPaginationVisiblePages = (
  page,
  onSide,
  pagesCount,
) => {
  let pages = [];

  // If number of pages is smaller than wanted number of shown items
  if (onSide * 2 + 1 > pagesCount) {
    for (let i = 1; i <= pagesCount; i += 1) {
      pages.push(i);
    }

    return pages;
  }

  for (let i = page - onSide; i <= page + onSide; i += 1) {
    pages.push(i);
  }
  // Shift onSide to the right
  while (pages[0] < 1) {
    pages = pages.map((val) => {
      return val + 1;
    });
  }
  // Shift onSide to the left
  while (pages[pages.length - 1] > pagesCount) {
    pages = pages.map((val) => {
      return val - 1;
    });
  }

  return pages;
};

/**
 * Format bytes as human-readable text.
 *
 * @param value Number of selected units.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param decimalPlaces Number of decimal places to display.
 * @param unit { String }
 * @return Formatted string.
 */
type HumanReadableFileSize = (
  value: number,
  si: boolean,
  decimalPlaces: number,
  unit: string,
) => string;
export const humanReadableFileSize: HumanReadableFileSize = (
  value,
  si,
  decimalPlaces = 1,
  unit,
) => {
  const thresh = si ? 1024 : 1000;

  let unitNivelator;
  switch (unit.toLowerCase()) {
    case 'B':
      unitNivelator = 0;
      break;
    case 'kb':
    case 'kib':
      unitNivelator = 1;
      break;
    case 'mb':
    case 'mib':
      unitNivelator = 2;
      break;
    case 'gb':
    case 'gib':
      unitNivelator = 3;
      break;
    case 'tb':
    case 'tib':
      unitNivelator = 4;
      break;
    case 'pb':
    case 'pib':
      unitNivelator = 5;
      break;
    case 'eb':
    case 'eib':
      unitNivelator = 6;
      break;
    case 'zb':
    case 'zib':
      unitNivelator = 7;
      break;
    case 'yb':
    case 'yib':
      unitNivelator = 8;
      break;
    default:
      unitNivelator = 0;
      break;
  }

  if (Math.abs(value) < thresh) {
    return `${value} B`;
  }

  const units = !si
    ? ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  // let u = -1 + unitNivelator;
  let u = unitNivelator;
  const r = 10 ** decimalPlaces;

  let valueCopy = value;
  do {
    valueCopy /= thresh;
    u += 1;
  } while (Math.round(Math.abs(valueCopy) * r) / r >= thresh && u < units.length - 1);

  return `${Math.round(valueCopy * 10 ** decimalPlaces) / 10 ** decimalPlaces} ${
    units[u]
  }`;
};

/**
 * Format integer -> shorter ~ version
 */

type shorterInteger = (integer: number, decimalPlaces: number) => string | number;
export const shorterInteger: shorterInteger = (integer, decimalPlaces = 1) => {
  const thresh = 1000;

  if (Math.abs(integer) < thresh) {
    return integer;
  }

  const units = ['k', 'M', 'B', 'T'];
  let u = -1;
  const r = 10 ** decimalPlaces;

  let integerCopy = integer;
  do {
    integerCopy /= thresh;
    u += 1;
  } while (Math.floor(Math.abs(integerCopy) * r) / r >= thresh && u < units.length - 1);

  return `${Math.floor(integerCopy * 10 ** decimalPlaces) / 10 ** decimalPlaces}${
    units[u]
  }+`;
};

type parseStorageProvider = <
  StorageProvider extends
    | ApiStorageProvider
    | AlgoliaStorageProvider
    | AlgoliaSearchStorageProviderHit,
>(
  details: StorageProvider,
) => StorageProvider;
export const parseStorageProvider: parseStorageProvider = (details) => ({
  ...details,
  name: details.name || details.ownerAddress.IDAddress,
});

export function appendFilUnit(price: string | number): string {
  return `${price} FIL`;
}

export const appendNanoFilPerGBUnit = (price: string | number): ReactElement => {
  return (
    <>
      {price} <span>nFIL/GB</span>
    </>
  );
};

export function getFilPriceFromBig(attoFil: Big): string {
  const price = toFilUpdateAndFormat(attoFil, (price) => price, 2);

  return appendFilUnit(price);
}

export function getFilPriceFromString(attoFilString: string): string {
  const price = getFormattedFilAmountFromString(attoFilString);

  return price.ok ? appendFilUnit(price.value) : 'unknown';
}

type FormattedFil =
  | {
      ok: false;
    }
  | {
      ok: true;
      value: string;
    };

export function getFormattedFilAmountFromString(attoFilString: string): FormattedFil {
  return getModifiedFormattedFilAmountFromString(attoFilString, (price) => price, 2);
}

export function getFormattedFilAmount(attoFil: Big, decimalPlaces: number): string {
  return toFilUpdateAndFormat(attoFil, (price) => price, decimalPlaces);
}

/**
 *  Formats attoFil amount to Fil, rounded to decimalPlaces
 *  and previously modified by the updatePrice() callback
 */
export function getModifiedFormattedFilAmountFromString(
  attoFilString: string | null,
  updatePrice: (price: Big) => Big,
  decimalPlaces: number,
): FormattedFil {
  try {
    if (!attoFilString) return { ok: false };
    const attoFil = Big(attoFilString);

    const value = toFilUpdateAndFormat(attoFil, updatePrice, decimalPlaces);

    return { ok: true, value };
  } catch (error) {
    return { ok: false };
  }
}

function toFilUpdateAndFormat(
  attoFil: Big,
  updatePrice: (price: Big) => Big,
  decimalPlaces: number,
) {
  const fil = attoFilToNanoFil(attoFil);
  const updatedPrice = updatePrice(fil);

  const price = formatNumber(updatedPrice, decimalPlaces);

  return price;
}

function attoFilToNanoFil(attoFil: Big): Big {
  return attoFil.div(10 ** 9);
}

// Rounds to number of decimal places.
// If rounded number is 0, rounds to first non-zero decimal place
function formatNumber(number: Big, decimalPlaces: number) {
  if (number.eq(0)) {
    return '0';
  }

  Big.RM = Big.roundDown;

  let depth = 0;
  do {
    depth += 1;
  } while (
    number
      .times(10 ** depth)
      .round()
      .lte(0)
  );

  const filPerGBString = number.toFixed(depth + decimalPlaces - 1);

  // Remove trailing zero(e)s
  return filPerGBString.replace(/\.?0+$/, '');
}

type GetIPFSimage = (cid: string) => string;
export const getIPFSimage: GetIPFSimage = (cid) => `${IPFS_GATEWAY}${cid}`;

type ConvertImgpathToFile = (imageObject: MediaFile) => Promise<File>;
export const convertImgpathToFile: ConvertImgpathToFile = (imageObject) => {
  const fileName = imageObject.filename;
  const fileType = `image/${fileName.slice(fileName.lastIndexOf('.') + 1)}`;
  const path = getIPFSimage(imageObject.cid);

  return fetch(path).then((res) =>
    res.arrayBuffer().then((buf) => new File([buf], fileName, { type: fileType })),
  );
};

type EpochToDate = (epoch: number) => Date;
export const epochToDate: EpochToDate = (epoch) => {
  const date = new Date();
  date.setTime(epochToUnixTime(epoch) * 1000);

  return date;
};

const FILECOIN_GENESIS_UNIX_EPOCH = 1598306400;

function epochToUnixTime(epoch: number) {
  return epoch * 30 + FILECOIN_GENESIS_UNIX_EPOCH;
}

// Utility functions used for a more precise type inference

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export function entries<T extends Object>(obj: T): Entries<T> {
  return Object.entries(obj) as any;
}

export function update<T>(state: T, updates: Partial<T>): T {
  return { ...state, ...updates };
}

/**
 * Formats the data center location by country and region
 *
 * @param country {String}
 * @param region {String}
 */
export const getDatacenterLocationText = ({
  country,
  region,
}: DataCenter | DealDataCenter | AlgoliaSearchHit): string => {
  return country && region ? `${country}, ${region}` : country || region || 'unknown';
};

/**
 * Appends formatted date to epoch data
 *
 * @param epoch {Number}
 */
type getFormattedEpochString = (epoch: number) => string;
export const getFormattedEpochString: getFormattedEpochString = (epoch) => {
  const startEpochDate = epochToDate(epoch);
  const formattedDate = format(startEpochDate, 'd.M.yyyy h:mm a');

  return `${epoch} - ${formattedDate}`;
};
export const getRandomColor = (): string => `#${Math.random().toString(16).substr(-6)}`;

export type ChartData = {
  [minerId: string]: {
    [minerId: string]: number;
    createdAt: number;
  }[];
};
type DashboardChartDataMapper = (payment: RewardsPayout[]) => ChartData;
export const dashboardChartDataMapper: DashboardChartDataMapper = (payment) =>
  _.mapValues(
    _.groupBy(payment, (p) => p.minerId),
    (data) =>
      data.map((d) => ({
        [d.minerId]: Number(d.amount),
        createdAt: getUnixTime(new Date(d.createdAt)),
      })),
  );

type GetLineColors = (data: ChartData) => string[];
export const getLineColors: GetLineColors = (data) =>
  entries(data).map((cd, index) => {
    return LINE_COLORS[index % LINE_COLORS.length];
  });

export const formatDateAsApiParameter = (date: Date): string =>
  format(date, 'yyyy-MM-dd HH:mm:ss');

type LoadedImage = { src: string; width: number; height: number };
export const chooseImageForTheme = (imagesByTheme: {
  [key in Theme]: LoadedImage;
}): LoadedImage => {
  return imagesByTheme[getLocalStorageData(LS_THEME_DATA_KEY, 'dark')];
};

export const convertBytestoMiB = (sizeInBytes?: number): string =>
  sizeInBytes ? `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MiB` : '0 MiB';

export const isStringValidNumber = (number: number): boolean => {
  // return /^-?\d+$/.test(string) || /^[+-]?\d+(\.\d+)?$/.test(string);
  return !Number.isNaN(number) && Number.isFinite(number);
};

export const isStorageProviderRoute = (pathname: string): boolean => {
  return STORAGE_PROVIDER_ROLE_LINKS.some((link) => pathname.includes(link));
};

export const isClientRoute = (pathname: string): boolean => {
  return CLIENT_ROLE_LINKS.some((route: string) => route === pathname);
};

export const isRouteProtected = (pathname: string): boolean => {
  return isStorageProviderRoute(pathname) || isClientRoute(pathname);
};

export const letterAlphabetPosition = (text: string): number => {
  return (parseInt(text, 36) - 10) % 10;
};

export const getEpochString = (epoch: number, timestamp: number): string => {
  const date = format(fromUnixTime(timestamp), 'dd.MM.yyyy h:mm a');

  return `${epoch} - ${date}`;
};

type Link = {
  label: string;
  url: string;
};
export const getAuthenticatedMenuItems = (isStorageProvider: boolean): Link[] => [
  {
    label: 'dashboard',
    url: isStorageProvider ? ROUTES.DASHBOARD.MY_PROFILES : ROUTES.DASHBOARD.MY_DEALS,
  },
  {
    label: 'accountSettings',
    url: ROUTES.DASHBOARD.ACCOUNT_SETTINGS,
  },
  {
    label: 'connectedAddresses',
    url: ROUTES.DASHBOARD.CONNECTED_ADDRESSES,
  },
];

export type LocalStorageTypes = {
  [LS_AUTH_DATA_KEY]: {
    token: string;
    walletAddress: string;
  } | null;
  [LS_THEME_DATA_KEY]: Theme;
  [LS_USER_SEEN_PAPERCUPS]: boolean;
  [HAS_SEEN_INITIAL_FILMINE_ADDRESS_POPUP]: boolean;
};

export function getLocalStorageData<Key extends keyof LocalStorageTypes>(
  item: Key,
  fallbackValue: LocalStorageTypes[Key],
): LocalStorageTypes[Key] {
  const lsData = localStorage.getItem(item);

  return lsData ? (JSON.parse(lsData) as LocalStorageTypes[Key]) : fallbackValue;
}

export function setLocalStorageData<Key extends keyof LocalStorageTypes>(
  item: Key,
  value: LocalStorageTypes[Key],
): void {
  const stringifiedValue = JSON.stringify(value);
  localStorage.setItem(item, stringifiedValue);
}

export const removeLocalStorageData = <Key extends keyof LocalStorageTypes>(
  item: Key,
): void => localStorage.removeItem(item);

export const isDashboardRoute = (path: string): boolean => path.includes('/dashboard');

export function getUpdatedValues<T>(
  values: NonNullable<T>,
  initialValues: NonNullable<T>,
): Partial<NonNullable<T>> {
  return Object.fromEntries(
    entries(values).filter(([key, value]) => initialValues[key] !== value),
  ) as Partial<NonNullable<T>>;
}
